import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { deductCredit } from '../api/credits';

const reasons = [
  { label: '오지급 회수', code: 'IncorrectGrant' },
  { label: '중복 지급 회수', code: 'DuplicateGrant' },
  { label: '테스트 지급 회수', code: 'TestGrant' },
  { label: '기타', code: 'Other' },
];

export default function CreditDeductionModal({ user, onClose, onSuccess, allowed = false }) {
  const { t } = useLanguage();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [finished, setFinished] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const inFlight = useRef(false);
  const dialogRef = useRef(null);
  const balance = Number(user.wallets?.[0]?.balance);
  const hasBalance = user.wallets?.[0]?.balance != null && Number.isFinite(balance);
  const positiveInteger = amount !== '' && Number.isSafeInteger(Number(amount)) && Number(amount) > 0;
  const maxDeduction = hasBalance ? Math.min(2147483647, Math.max(0, Math.ceil(balance) - 1)) : 0;
  const canDeduct = hasBalance && maxDeduction >= 1;
  const validAmount = positiveInteger && canDeduct && Number(amount) <= maxDeduction;
  const canSubmit = allowed && Boolean(user.userUuid) && validAmount && reasons.some(item => item.code === reason) && !submitting && !finished && !uncertain;
  const amountError = !hasBalance
    ? '현재 잔액을 확인할 수 없어 차감할 수 없습니다.'
    : !canDeduct
      ? '잔액이 1크레딧 이하이므로 차감할 수 없습니다.'
      : amount === ''
        ? ''
        : !positiveInteger
          ? '1 이상의 정수를 입력해주세요.'
          : Number(amount) > 2147483647
            ? '차감 수량은 2,147,483,647 이하여야 합니다.'
          : !validAmount
            ? '차감 후 잔액은 0보다 커야 합니다. 현재 잔액보다 적은 수량을 입력해주세요.'
            : '';

  const close = () => {
    if (!inFlight.current) onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError('');
    let saved = false;
    try {
      const result = await deductCredit({ userUuid: user.userUuid, amount: Number(amount), deductionReason: reason });
      saved = true;
      setFinished(true);
      await onSuccess?.(result);
    } catch (err) {
      if (saved) {
        setError(t('차감은 완료되었지만 화면 갱신에 실패했습니다. 다시 차감하지 말고 새로고침해주세요.'));
      } else if (!err.response || err.response.status >= 500 || err.response.status === 408) {
        setUncertain(true);
        setError(t('차감 결과를 확인할 수 없습니다. 중복 차감을 피하려면 창을 닫고 잔액과 거래내역을 확인해주세요.'));
      } else {
        const message = err.response?.data?.message;
        setError(Array.isArray(message) ? message.join(', ') : message || err.message || t('크레딧 차감에 실패했습니다.'));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const previousFocus = document.activeElement;
    dialogRef.current?.focus();
    return () => previousFocus?.focus();
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') close();
    if (event.key !== 'Tab') return;
    const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled)')].filter(control => !control.disabled);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={close}></div>
      <div className="modal deduction-modal" role="dialog" aria-modal="true" aria-labelledby="deduction-title" tabIndex={-1} ref={dialogRef} onKeyDown={handleKeyDown}>
        <div className="modal-header">
          <h3 id="deduction-title">{t('크레딧 차감')}</h3>
          <button type="button" className="drawer-close-btn" aria-label={t('닫기')} disabled={submitting} onClick={close}>×</button>
        </div>
        <div className="modal-body">
          <div className="deduction-user-summary">
            <strong>{user.username}</strong>
            <span>{user.email}</span>
            <small>User UUID: {user.userUuid}</small>
            <div>{t('현재 잔액')} <strong>{hasBalance ? balance.toLocaleString() : '-'} credit</strong></div>
          </div>
          <div className="modal-field">
            <label htmlFor="deduction-amount">{t('차감 수량')}</label>
            <input id="deduction-amount" type="number" min="1" max={maxDeduction} step="1" placeholder="100" value={amount} disabled={!canDeduct || submitting || finished || uncertain} aria-invalid={Boolean(amountError)} aria-describedby={amountError ? 'deduction-amount-error' : undefined} onChange={(event) => setAmount(event.target.value)} />
            {amountError && <span id="deduction-amount-error" className="deduction-hint" role="alert">{t(amountError)}</span>}
          </div>
          <fieldset className="deduction-reasons" disabled={submitting || finished || uncertain}>
            <legend>{t('차감 사유')}</legend>
            {reasons.map((item) => (
              <label key={item.code} className={reason === item.code ? 'selected' : ''}>
                <input type="radio" name="credit-deduction-reason" value={item.code} checked={reason === item.code} onChange={() => setReason(item.code)} />
                <span>{t(item.label)}</span>
              </label>
            ))}
          </fieldset>
          <div className="deduction-preview">
            <span>{t('예상 차감 후 잔액')}</span>
            <strong>{hasBalance && validAmount ? (balance - Number(amount)).toLocaleString() : '-'} credit</strong>
          </div>
          <p className="modal-warning">{t('선택한 사용자의 크레딧이 실제로 차감됩니다. 수량과 사유를 확인해주세요.')}</p>
          {error && <p className="modal-error" role="alert">{error}</p>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" disabled={submitting} onClick={close}>{t('취소')}</button>
          <button type="button" className="credit-deduction-btn" disabled={!canSubmit} onClick={handleSubmit}>{t(submitting ? '차감 중...' : '차감하기')}</button>
        </div>
      </div>
    </>
  );
}
